/**
 * Project Departments tab.
 *
 * Renders the client-side project department assignment headings:
 * Procurement, Logistic, User Department, QA, and Finance. Each heading can select one
 * department or sub-department from the project's client and then maintain
 * project-specific contacts.
 */
import { useState } from 'react';
import { Building2, Phone, Plus, Star, Trash2, Users } from 'lucide-react';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { FormField, Input } from '@/components/ui/form-field';
import { Modal } from '@/components/ui/modal';
import { Select } from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { useCurrentUser } from '@/lib/auth';
import { isLockedForUser } from '@/lib/closure-lock';
import {
  type DepartmentRole,
  type ProjectDepartmentContact,
  type ProjectDepartmentWithContacts,
  useAddContact,
  useAddProjectDepartment,
  useDepartmentsList,
  useProjectDepartments,
  useRemoveContact,
  useRemoveProjectDepartment,
  useUpdateContact,
} from '@/lib/project-queries';
import type { Project } from '@/lib/project-queries';

interface RoleConfig {
  role: DepartmentRole;
  title: string;
  body: string;
  icon: React.ReactNode;
}

function roleHeadingLabel(role: DepartmentRole): string {
  if (role === 'LOGISTICS') return 'Logistic';
  if (role === 'USER') return 'User Department';
  return role.charAt(0) + role.slice(1).toLowerCase();
}

const ROLE_CONFIGS: RoleConfig[] = [
  {
    role: 'PROCUREMENT',
    title: 'Procurement',
    body: 'Select the client department or sub-department responsible for procurement coordination.',
    icon: <Building2 className="h-4 w-4" />,
  },
  {
    role: 'LOGISTICS',
    title: 'Logistic',
    body: 'Select the client department or sub-department responsible for shipment and delivery coordination.',
    icon: <Building2 className="h-4 w-4" />,
  },
  {
    role: 'USER',
    title: 'User Department',
    body: 'Select the end-user department or sub-department for this project.',
    icon: <Users className="h-4 w-4" />,
  },
  {
    role: 'QA',
    title: 'QA',
    body: 'Select the quality assurance or inspection department/sub-department.',
    icon: <Building2 className="h-4 w-4" />,
  },
  {
    role: 'FINANCE',
    title: 'Finance',
    body: 'Select the client finance department or sub-department for payments and financial correspondence.',
    icon: <Building2 className="h-4 w-4" />,
  },
];

export function ProjectDepartmentsTab({ project }: { project: Project }) {
  const projectId = project.id;
  const departments = useProjectDepartments(projectId);

  if (departments.isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner className="h-6 w-6 text-secondary" />
      </div>
    );
  }

  const all = departments.data ?? [];

  return (
    <div className="space-y-4">
      {ROLE_CONFIGS.map((cfg) => {
        const forRole = all.filter((d) => d.departmentRole === cfg.role);
        return (
          <DepartmentRoleBlock
            key={cfg.role}
            project={project}
            config={cfg}
            existing={forRole}
            allRoles={all}
          />
        );
      })}
    </div>
  );
}

function DepartmentRoleBlock({
  project,
  config,
  existing,
  allRoles,
}: {
  project: Project;
  config: RoleConfig;
  existing: ProjectDepartmentWithContacts[];
  allRoles: ProjectDepartmentWithContacts[];
}) {
  const me = useCurrentUser();
  const isAdmin = me.data?.user.role === 'ADMIN';
  const [attachOpen, setAttachOpen] = useState(false);
  const showAttach = existing.length === 0;
  // Spec §9 — closed projects lock attach/detach against Assistants too.
  const locked = isLockedForUser(project, isAdmin);

  return (
    <Card
      title={
        <span className="inline-flex items-center gap-2">
          {config.icon}
          {config.title}
          {existing.length > 0 ? <Badge tone="neutral">Assigned</Badge> : null}
        </span>
      }
      actions={
        isAdmin && showAttach ? (
          <Button
            size="compact"
            variant="secondary"
            leftIcon={<Plus className="h-3.5 w-3.5" />}
            onClick={() => setAttachOpen(true)}
            disabled={locked}
          >
            Attach department
          </Button>
        ) : null
      }
    >
      {existing.length === 0 ? (
        <EmptyState title="No department assigned" body={config.body} className="py-6" />
      ) : (
        <div className="space-y-3">
          {existing.map((dept) => (
            <DepartmentInstance
              key={dept.id}
              projectId={project.id}
              project={project}
              department={dept}
              isAdmin={isAdmin}
              isSingular
            />
          ))}
        </div>
      )}

      <AttachDepartmentModal
        open={attachOpen}
        onClose={() => setAttachOpen(false)}
        projectId={project.id}
        role={config.role}
        projectClientId={project.clientId}
        alreadyAttachedDepartmentIds={allRoles.map((r) => r.departmentId)}
      />
    </Card>
  );
}

function DepartmentInstance({
  projectId,
  project,
  department,
  isAdmin,
  isSingular,
}: {
  projectId: string;
  project: Project;
  department: ProjectDepartmentWithContacts;
  isAdmin: boolean;
  isSingular: boolean;
}) {
  const remove = useRemoveProjectDepartment(projectId);
  const [contactOpen, setContactOpen] = useState(false);
  // Spec §9 — closed projects lock edits against Assistants only.
  const locked = isLockedForUser(project, isAdmin);

  return (
    <div className="rounded-md border border-border-default p-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-sm font-medium text-primary">
            {department.departmentParentName
              ? `${department.departmentParentName} / ${department.departmentName}`
              : department.departmentName}
          </div>
          {department.contacts.some((c) => c.isPrimary) && !isSingular ? (
            <div className="mt-0.5 text-xs text-secondary">
              Primary contact: {department.contacts.find((c) => c.isPrimary)?.name}
            </div>
          ) : null}
        </div>
        <div className="flex items-center gap-1">
          {/* Spec §13 — both roles can edit project contacts. */}
          <Button
            size="compact"
            variant="tertiary"
            leftIcon={<Plus className="h-3 w-3" />}
            onClick={() => setContactOpen(true)}
            disabled={locked}
          >
            Add contact
          </Button>
          {isAdmin && department.contacts.length === 0 ? (
            <Button
              size="compact"
              variant="tertiary"
              leftIcon={<Trash2 className="h-3 w-3" />}
              loading={remove.isPending}
              onClick={() => remove.mutate({ projectDepartmentId: department.id })}
              aria-label="Detach"
            >
              Detach
            </Button>
          ) : null}
        </div>
      </div>

      {remove.error ? (
        <Alert tone="danger" className="mt-2">
          {remove.error.message}
        </Alert>
      ) : null}

      {department.contacts.length > 0 ? (
        <ul className="mt-3 divide-y divide-border-default">
          {department.contacts.map((c) => (
            <ContactRow
              key={c.id}
              projectId={projectId}
              projectDepartmentId={department.id}
              contact={c}
              locked={locked}
            />
          ))}
        </ul>
      ) : (
        <p className="mt-2 text-xs text-tertiary">No contacts yet.</p>
      )}

      <AddContactModal
        open={contactOpen}
        onClose={() => setContactOpen(false)}
        projectId={projectId}
        projectDepartmentId={department.id}
        canMarkPrimary={!isSingular}
        hasOtherContacts={department.contacts.length > 0}
      />
    </div>
  );
}

function ContactRow({
  projectId,
  projectDepartmentId,
  contact,
  locked,
}: {
  projectId: string;
  projectDepartmentId: string;
  contact: ProjectDepartmentContact;
  locked: boolean;
}) {
  const remove = useRemoveContact(projectId);
  const [editOpen, setEditOpen] = useState(false);
  return (
    <li className="flex items-center justify-between gap-3 py-2">
      <div className="min-w-0">
        <div className="flex items-center gap-2 text-sm">
          <span className="font-medium text-primary">{contact.name}</span>
          {contact.designation ? (
            <span className="text-secondary">— {contact.designation}</span>
          ) : null}
          {contact.isPrimary ? (
            <Badge tone="info" className="gap-1">
              <Star className="h-3 w-3" /> Primary
            </Badge>
          ) : null}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-secondary">
          {contact.officialPhone ? (
            <span className="inline-flex items-center gap-1 tabular-nums">
              <Phone className="h-3 w-3" /> {contact.officialPhone} (phone)
            </span>
          ) : null}
          {contact.personalPhone ? (
            <span className="inline-flex items-center gap-1 tabular-nums">
              <Phone className="h-3 w-3" /> {contact.personalPhone} (personal)
            </span>
          ) : null}
          {contact.email ? <span>Email: {contact.email}</span> : null}
          {contact.fax ? <span>Fax: {contact.fax}</span> : null}
          {!contact.officialPhone && !contact.personalPhone && !contact.email && !contact.fax ? (
            <span className="text-tertiary">No phone, email, or fax</span>
          ) : null}
        </div>
      </div>
      {/* Spec §13 — both roles can edit project contacts. The closure
          gate (locked) is the only thing that hides the affordance. */}
      <div className="flex items-center gap-1">
        <Button
          size="compact"
          variant="tertiary"
          disabled={locked}
          onClick={() => setEditOpen(true)}
          aria-label="Edit contact"
        >
          Edit
        </Button>
        <Button
          size="compact"
          variant="tertiary"
          loading={remove.isPending}
          disabled={locked}
          onClick={() =>
            remove.mutate({
              projectDepartmentId,
              contactId: contact.id,
              rowVersion: contact.rowVersion,
            })
          }
          aria-label="Remove contact"
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
      <EditContactModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        projectId={projectId}
        projectDepartmentId={projectDepartmentId}
        contact={contact}
      />
    </li>
  );
}

// ---------------------------------------------------------------------------
// Attach department modal
// ---------------------------------------------------------------------------

function AttachDepartmentModal({
  open,
  onClose,
  projectId,
  projectClientId,
  role,
  alreadyAttachedDepartmentIds,
}: {
  open: boolean;
  onClose: () => void;
  projectId: string;
  projectClientId: string;
  role: DepartmentRole;
  alreadyAttachedDepartmentIds: string[];
}) {
  const departments = useDepartmentsList({ clientId: projectClientId, isActive: true });
  const add = useAddProjectDepartment(projectId);
  const [departmentId, setDepartmentId] = useState('');

  const departmentNames = new Map((departments.data ?? []).map((d) => [d.id, d.name]));
  const available =
    departments.data
      ?.filter((d) => !alreadyAttachedDepartmentIds.includes(d.id))
      .sort((a, b) => {
        const aParent = a.parentDepartmentId ? (departmentNames.get(a.parentDepartmentId) ?? '') : '';
        const bParent = b.parentDepartmentId ? (departmentNames.get(b.parentDepartmentId) ?? '') : '';
        return `${aParent}/${a.name}`.localeCompare(`${bParent}/${b.name}`);
      }) ?? [];

  const departmentLabel = (department: { name: string; parentDepartmentId: string | null }) => {
    const parentName = department.parentDepartmentId
      ? departmentNames.get(department.parentDepartmentId)
      : null;
    return parentName ? `${parentName} / ${department.name}` : department.name;
  };

  const submit = (): void => {
    if (!departmentId) return;
    add.mutate(
      { departmentId, departmentRole: role },
      {
        onSuccess: () => {
          setDepartmentId('');
          onClose();
        },
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Attach department"
      size="sm"
      footer={
        <>
          <Button variant="tertiary" onClick={onClose} disabled={add.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            disabled={!departmentId}
            loading={add.isPending}
          >
            Attach
          </Button>
        </>
      }
    >
      {add.error ? (
        <Alert tone="danger" className="mb-3">
          {add.error.message}
        </Alert>
      ) : null}
      <FormField label={`Department / sub-department (${roleHeadingLabel(role)} heading)`} required>
        {departments.isLoading ? (
          <Spinner className="h-4 w-4" />
        ) : available.length === 0 ? (
          <p className="text-sm text-secondary">
            No active departments or sub-departments for this project client are available.
          </p>
        ) : (
          <Select value={departmentId} onChange={(event) => setDepartmentId(event.target.value)}>
            <option value="">Select a department…</option>
            {available.map((d) => (
              <option key={d.id} value={d.id}>
                {departmentLabel(d)}
              </option>
            ))}
          </Select>
        )}
      </FormField>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Add contact modal
// ---------------------------------------------------------------------------

function AddContactModal({
  open,
  onClose,
  projectId,
  projectDepartmentId,
  canMarkPrimary,
  hasOtherContacts,
}: {
  open: boolean;
  onClose: () => void;
  projectId: string;
  projectDepartmentId: string;
  canMarkPrimary: boolean;
  hasOtherContacts: boolean;
}) {
  const add = useAddContact(projectId);
  const [name, setName] = useState('');
  const [designation, setDesignation] = useState('');
  const [officialPhone, setOfficialPhone] = useState('');
  const [personalPhone, setPersonalPhone] = useState('');
  const [email, setEmail] = useState('');
  const [fax, setFax] = useState('');
  // First contact in a block defaults to primary; subsequent contacts do not.
  const [isPrimary, setIsPrimary] = useState(canMarkPrimary && !hasOtherContacts);

  const submit = (): void => {
    const trimmedName = name.trim();
    if (!trimmedName) return;
    add.mutate(
      {
        projectDepartmentId,
        name: trimmedName,
        designation: designation.trim() || undefined,
        officialPhone: officialPhone.trim() || undefined,
        personalPhone: personalPhone.trim() || undefined,
        email: email.trim() || undefined,
        fax: fax.trim() || undefined,
        isPrimary,
      },
      {
        onSuccess: () => {
          setName('');
          setDesignation('');
          setOfficialPhone('');
          setPersonalPhone('');
          setEmail('');
          setFax('');
          setIsPrimary(canMarkPrimary && !hasOtherContacts);
          onClose();
        },
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add contact"
      size="md"
      footer={
        <>
          <Button variant="tertiary" onClick={onClose} disabled={add.isPending}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} disabled={!name.trim()} loading={add.isPending}>
            Add contact
          </Button>
        </>
      }
    >
      {add.error ? (
        <Alert tone="danger" className="mb-3">
          {add.error.message}
        </Alert>
      ) : null}
      <div className="grid gap-3 sm:grid-cols-2">
        <FormField label="Name" required htmlFor="contact-name">
          <Input
            id="contact-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            autoFocus
          />
        </FormField>
        <FormField label="Designation" htmlFor="contact-designation">
          <Input
            id="contact-designation"
            value={designation}
            onChange={(event) => setDesignation(event.target.value)}
          />
        </FormField>
        <FormField label="Phone" htmlFor="official-phone">
          <Input
            id="official-phone"
            type="tel"
            value={officialPhone}
            onChange={(event) => setOfficialPhone(event.target.value)}
            className="tabular-nums"
          />
        </FormField>
        <FormField label="Personal phone" htmlFor="personal-phone">
          <Input
            id="personal-phone"
            type="tel"
            value={personalPhone}
            onChange={(event) => setPersonalPhone(event.target.value)}
            className="tabular-nums"
          />
        </FormField>
        <FormField label="Email" htmlFor="contact-email">
          <Input
            id="contact-email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </FormField>
        <FormField label="Fax" htmlFor="contact-fax">
          <Input
            id="contact-fax"
            value={fax}
            onChange={(event) => setFax(event.target.value)}
            className="tabular-nums"
          />
        </FormField>
      </div>
      {canMarkPrimary ? (
        <label className="mt-3 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={isPrimary}
            onChange={(event) => setIsPrimary(event.target.checked)}
            className="h-4 w-4 rounded-sm border-border-default"
          />
          Mark as primary contact (other contacts in this block will be demoted)
        </label>
      ) : null}
    </Modal>
  );
}

function EditContactModal({
  open,
  onClose,
  projectId,
  projectDepartmentId,
  contact,
}: {
  open: boolean;
  onClose: () => void;
  projectId: string;
  projectDepartmentId: string;
  contact: ProjectDepartmentContact;
}) {
  const update = useUpdateContact(projectId);
  const [name, setName] = useState(contact.name);
  const [designation, setDesignation] = useState(contact.designation ?? '');
  const [officialPhone, setOfficialPhone] = useState(contact.officialPhone ?? '');
  const [personalPhone, setPersonalPhone] = useState(contact.personalPhone ?? '');
  const [email, setEmail] = useState(contact.email ?? '');
  const [fax, setFax] = useState(contact.fax ?? '');
  const [isPrimary, setIsPrimary] = useState(contact.isPrimary);

  const submit = (): void => {
    const trimmedName = name.trim();
    if (!trimmedName) return;
    update.mutate(
      {
        projectDepartmentId,
        contactId: contact.id,
        name: trimmedName,
        designation: designation.trim() || null,
        officialPhone: officialPhone.trim() || null,
        personalPhone: personalPhone.trim() || null,
        email: email.trim() || null,
        fax: fax.trim() || null,
        isPrimary,
        rowVersion: contact.rowVersion,
      },
      { onSuccess: onClose },
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit contact"
      size="md"
      footer={
        <>
          <Button variant="tertiary" onClick={onClose} disabled={update.isPending}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} disabled={!name.trim()} loading={update.isPending}>
            Save changes
          </Button>
        </>
      }
    >
      {update.error ? (
        <Alert tone="danger" className="mb-3">
          {update.error.message}
        </Alert>
      ) : null}
      <div className="grid gap-3 sm:grid-cols-2">
        <FormField label="Name" required htmlFor="edit-contact-name">
          <Input
            id="edit-contact-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            autoFocus
          />
        </FormField>
        <FormField label="Designation" htmlFor="edit-contact-designation">
          <Input
            id="edit-contact-designation"
            value={designation}
            onChange={(event) => setDesignation(event.target.value)}
          />
        </FormField>
        <FormField label="Phone" htmlFor="edit-official-phone">
          <Input
            id="edit-official-phone"
            type="tel"
            value={officialPhone}
            onChange={(event) => setOfficialPhone(event.target.value)}
            className="tabular-nums"
          />
        </FormField>
        <FormField label="Personal phone" htmlFor="edit-personal-phone">
          <Input
            id="edit-personal-phone"
            type="tel"
            value={personalPhone}
            onChange={(event) => setPersonalPhone(event.target.value)}
            className="tabular-nums"
          />
        </FormField>
        <FormField label="Email" htmlFor="edit-contact-email">
          <Input
            id="edit-contact-email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </FormField>
        <FormField label="Fax" htmlFor="edit-contact-fax">
          <Input
            id="edit-contact-fax"
            value={fax}
            onChange={(event) => setFax(event.target.value)}
            className="tabular-nums"
          />
        </FormField>
      </div>
      <label className="mt-3 flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={isPrimary}
          onChange={(event) => setIsPrimary(event.target.checked)}
          className="h-4 w-4 rounded-sm border-border-default"
        />
        Mark as primary contact (other contacts in this block will be demoted)
      </label>
    </Modal>
  );
}
